import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { settings, navItems } = useSiteSettings();

  const phone = settings?.phone || '9585 8258';
  const companyName = settings?.company_name || '智駕駛';
  const companyNameEn = settings?.company_name_en || 'Wise Driving';
  const rawLogoUrl = settings?.logo_url;
  const logoHeight = settings?.logo_height || 40;
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const logoUrl = rawLogoUrl
    ? rawLogoUrl.startsWith('http') || rawLogoUrl.startsWith('data:')
      ? rawLogoUrl
      : rawLogoUrl.startsWith('/api/')
        ? `${BASE_URL}${rawLogoUrl}`
        : rawLogoUrl
    : '';

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center" style={{ minHeight: `${logoHeight + 24}px` }}>
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="object-contain" style={{height: `${logoHeight}px`}} />
            ) : (
              <>
                <div className="text-2xl font-bold text-primary">{companyName}</div>
                <div className="hidden sm:block text-sm text-muted-foreground">{companyNameEn}</div>
              </>
            )}
          </Link>

          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label}`}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-primary text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <a href={`tel:${phone.replace(/\s+/g, '').startsWith('+') ? phone.replace(/\s+/g, '') : `+852${phone.replace(/\s+/g, '')}`}`} className="flex items-center text-sm text-gray-600 hover:text-primary transition-colors">
              <Phone className="h-4 w-4 mr-2" />
              {phone}
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive(link.path)
                    ? 'bg-primary text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={`tel:${phone.replace(/\s+/g, '').startsWith('+') ? phone.replace(/\s+/g, '') : `+852${phone.replace(/\s+/g, '')}`}`}
              className="flex items-center px-3 py-2 text-base text-gray-600"
            >
              <Phone className="h-5 w-5 mr-2" />
              {phone}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
